import React, { useEffect, useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { FiUsers, FiFilm, FiBook, FiPlusCircle, FiActivity, FiLayers, FiEdit3 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { userService, materialService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMaterials: 0,
    totalVideos: 0,
    totalBooks: 0,
    totalMagazines: 0,
  });
  const [loading, setLoading] = useState(true);
  const [greetingTime, setGreetingTime] = useState('');

  useEffect(() => {
    // Determina o cumprimento baseado na hora do dia
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreetingTime('Bom dia');
    } else if (hour >= 12 && hour < 18) {
      setGreetingTime('Boa tarde');
    } else {
      setGreetingTime('Boa noite');
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Busca usuários
        const { data: usersData, error: userError } = await userService.getUsers();
        
        if (userError) throw new Error(userError.message);
        
        // Busca materiais
        const { data: materialsData, error: materialsError } = await materialService.getMaterials();
        
        if (materialsError) throw new Error(materialsError.message);
        
        // Calcula estatísticas
        const videoCount = materialsData?.filter((m: any) => m.tipo === 'vídeo').length || 0;
        const bookCount = materialsData?.filter((m: any) => m.tipo === 'livro').length || 0;
        const magazineCount = materialsData?.filter((m: any) => m.tipo === 'revista').length || 0;
        
        setStats({
          totalUsers: usersData?.length || 0,
          totalMaterials: materialsData?.length || 0,
          totalVideos: videoCount,
          totalBooks: bookCount,
          totalMagazines: magazineCount
        });
      } catch (err) {
        console.error('Erro ao buscar estatísticas:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Usuários',
      value: stats.totalUsers,
      icon: <FiUsers size={24} />,
      link: '/usuarios',
      linkText: 'Gerenciar Usuários',
      color: 'from-blue-600/20 to-blue-800/20 border-blue-600/30'
    },
    {
      title: 'Total de Materiais',
      value: stats.totalMaterials,
      icon: <FiLayers size={24} />,
      link: '/materiais',
      linkText: 'Todos os Materiais',
      color: 'from-vermelho-claro/20 to-vermelho-escuro/20 border-vermelho-claro/30'
    },
    {
      title: 'Vídeos',
      value: stats.totalVideos,
      icon: <FiFilm size={24} />,
      link: '/materiais?filter=video',
      linkText: 'Ver Vídeos',
      color: 'from-purple-600/20 to-purple-800/20 border-purple-600/30'
    },
    {
      title: 'Livros e Revistas',
      value: stats.totalBooks + stats.totalMagazines,
      icon: <FiBook size={24} />,
      link: '/materiais?filter=book',
      linkText: 'Ver Publicações',
      color: 'from-green-600/20 to-green-800/20 border-green-600/30'
    }
  ];

  return (
    <MainLayout>
      {/* Cabeçalho com saudação */}
      <div className="relative bg-gray-900 rounded-xl p-6 mb-8 overflow-hidden shadow-md">
        <div className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-vermelho-escuro/20 to-transparent"></div>
        <div className="relative z-10">
          <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway mb-2">
            {greetingTime}, <span className="text-vermelho-claro">{user?.nome?.split(' ')[0]}</span>!
          </h1>
          <p className="text-gray-300 max-w-2xl">
            Bem-vindo ao painel administrativo do Portal do Cliente. Aqui você pode gerenciar usuários, 
            materiais e permissões da plataforma.
          </p>
        </div>
      </div>
      
      {/* Cards de estatísticas */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-900/70 rounded-lg h-[140px] animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <div 
              key={index} 
              className={`rounded-xl border bg-gradient-to-br ${card.color} p-6 flex flex-col h-full hover:shadow-lg hover:border-opacity-50 transition-all duration-300`}
            >
              <div className="flex items-center mb-4">
                <div className="p-2 bg-white/10 rounded-lg text-white">
                  {card.icon}
                </div>
                <h3 className="text-lg font-raleway font-semibold text-white ml-3">
                  {card.title}
                </h3>
              </div>
              <div className="text-3xl font-bold text-white mb-4">
                {card.value}
              </div>
              <Link 
                to={card.link} 
                className="text-white font-medium transition-colors mt-auto text-sm flex items-center group"
              >
                <span className="group-hover:underline">{card.linkText}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      )}
      
      {/* Ações rápidas */}
      <section className="mb-8">
        <h2 className="text-xl font-bold text-white font-raleway mb-5 flex items-center">
          <FiActivity className="mr-2 text-vermelho-claro" />
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link 
            to="/usuarios/novo" 
            className="flex items-center p-4 md:p-5 bg-gray-900 hover:bg-gray-800 rounded-lg transition-all duration-300 group border border-transparent hover:border-blue-600/30"
          >
            <div className="w-12 h-12 rounded-full bg-blue-900/40 flex items-center justify-center mr-4 group-hover:bg-blue-900/60 transition-colors">
              <FiUsers className="text-blue-400" size={20} />
            </div>
            <div>
              <h3 className="font-raleway font-medium text-white text-lg">Adicionar Usuário</h3>
              <p className="text-sm text-gray-400">Cadastre um novo usuário no sistema</p>
            </div>
          </Link>
          
          <Link 
            to="/materiais/novo" 
            className="flex items-center p-4 md:p-5 bg-gray-900 hover:bg-gray-800 rounded-lg transition-all duration-300 group border border-transparent hover:border-vermelho-claro/30"
          >
            <div className="w-12 h-12 rounded-full bg-vermelho-escuro/40 flex items-center justify-center mr-4 group-hover:bg-vermelho-escuro/60 transition-colors">
              <FiFilm className="text-vermelho-claro" size={20} />
            </div>
            <div>
              <h3 className="font-raleway font-medium text-white text-lg">Adicionar Material</h3>
              <p className="text-sm text-gray-400">Cadastre um novo vídeo ou publicação</p>
            </div>
          </Link>
          
          <Link 
            to="/permissoes" 
            className="flex items-center p-4 md:p-5 bg-gray-900 hover:bg-gray-800 rounded-lg transition-all duration-300 group border border-transparent hover:border-purple-600/30"
          >
            <div className="w-12 h-12 rounded-full bg-purple-900/40 flex items-center justify-center mr-4 group-hover:bg-purple-900/60 transition-colors">
              <FiEdit3 className="text-purple-400" size={20} />
            </div>
            <div>
              <h3 className="font-raleway font-medium text-white text-lg">Gerenciar Permissões</h3>
              <p className="text-sm text-gray-400">Defina o acesso aos materiais</p>
            </div>
          </Link>
        </div>
      </section>
      
      {/* Atividade Recente */}
      <section>
        <h2 className="text-xl font-bold text-white font-raleway mb-5 flex items-center">
          <FiActivity className="mr-2 text-vermelho-claro" />
          Atividade Recente
        </h2>
        <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
          <div className="flex items-center justify-center flex-col py-6">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <FiActivity className="text-gray-600" size={24} />
            </div>
            <p className="text-gray-400 text-center font-medium">
              O sistema de logs de atividades será implementado em breve.
            </p>
            <p className="text-gray-600 text-sm mt-2 text-center max-w-md">
              Aqui você poderá acompanhar as ações recentes realizadas por administradores 
              e usuários da plataforma.
            </p>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default Dashboard; 